import React, { Component } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';

import { searchOnlineDoctors, addDoctorToPool } from '../../services/api/pool-manager';
import { debounce } from '../../utils/debounce';
import { RegularText, BoldText } from '../common/Text';
import LoadingComponent from '../common/LoadingComponent';

const SearchInput = styled.input`
  font-family: BrandonText-Medium, Lato, sans-serif;
  padding: 10px 14px;
  width: 100%;
  border: 1px solid #ddd6ee;
  border-radius: 10px;
  font-size: 0.9rem;
  outline: none;
  :focus {
    border-color: #6b4bd6;
  }
`;

const Results = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 12px;
  max-height: 22rem;
  overflow-y: auto;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  margin-top: 6px;
  background: #fbfafe;
  border: 1px solid #eeecf6;
  border-radius: 10px;
`;

const Meta = styled.div`
  display: flex;
  flex-direction: column;
`;

const AddBtn = styled.button`
  font-family: BrandonText-Medium, Lato, sans-serif;
  font-size: 0.78rem;
  padding: 6px 14px;
  border: none;
  border-radius: 20px;
  color: white;
  background: linear-gradient(135deg, #6b4bd6, #4b2e83);
  cursor: pointer;
  :disabled {
    background: #c5c0d4;
    cursor: not-allowed;
  }
`;

const HelperText = styled(RegularText)`
  color: #8a84a0;
  margin-top: 10px;
`;

const MIN_QUERY_LENGTH = 2;

class AddDoctorModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      query: '',
      doctors: [],
      loading: false,
      searched: false,
      addingId: null,
    };
    this.debouncedSearch = debounce(this.runSearch, 500);
  }

  runSearch = (term) => {
    const trimmed = (term || '').trim();
    if (trimmed.length < MIN_QUERY_LENGTH) {
      this.setState({ doctors: [], loading: false, searched: false });
      return;
    }
    this.setState({ loading: true });
    searchOnlineDoctors(trimmed.toLowerCase())
      .then((res) => {
        if (res && res.message === 'success') {
          this.setState({ doctors: res.doctors || [], searched: true });
        } else {
          throw new Error((res && res.errorMessage) || 'Failed to search doctors');
        }
      })
      .catch((err) => {
        toast.error(err.message || 'Failed to search doctors');
        this.setState({ doctors: [], searched: true });
      })
      .finally(() => {
        this.setState({ loading: false });
      });
  };

  onChange = (e) => {
    const query = e.target.value;
    this.setState({ query });
    this.debouncedSearch(query);
  };

  onAdd = (doctor) => {
    const { poolId, onAdded } = this.props;
    this.setState({ addingId: doctor.doctorId });
    addDoctorToPool(poolId, doctor.doctorId)
      .then((res) => {
        if (res && res.message === 'success') {
          toast.success(`${doctor.doctorName || 'Doctor'} added to the pool.`);
          if (onAdded) {
            onAdded();
          }
        } else {
          throw new Error((res && res.errorMessage) || 'Failed to add doctor');
        }
      })
      .catch((err) => {
        toast.error(err.message || 'Failed to add doctor');
      })
      .finally(() => {
        this.setState({ addingId: null });
      });
  };

  render() {
    const { isOpen, poolName, onClose } = this.props;
    const { query, doctors, loading, searched, addingId } = this.state;

    return (
      <Modal isOpen={isOpen} toggle={onClose} size="lg" centered>
        <ModalHeader toggle={onClose}>
          {`Add doctor to ${poolName || 'pool'}`}
        </ModalHeader>
        <ModalBody>
          <SearchInput
            autoFocus
            value={query}
            placeholder="Search by doctor name or ID"
            onChange={this.onChange}
          />
          {loading ? (
            <LoadingComponent />
          ) : (
            <Results>
              {doctors.map((doctor) => (
                <Row key={`${doctor.doctorId}-${doctor.practiceId}`}>
                  <Meta>
                    <BoldText>{doctor.doctorName}</BoldText>
                    <RegularText color="#7a7391">
                      {`ID: ${doctor.doctorId}`}
                      {doctor.centerName ? ` · ${doctor.centerName}` : ''}
                    </RegularText>
                  </Meta>
                  <AddBtn
                    disabled={`${addingId}` === `${doctor.doctorId}`}
                    onClick={() => this.onAdd(doctor)}
                  >
                    {`${addingId}` === `${doctor.doctorId}` ? 'Adding…' : 'Add'}
                  </AddBtn>
                </Row>
              ))}
              {searched && !doctors.length ? (
                <HelperText>{`No doctors found for "${query}".`}</HelperText>
              ) : null}
              {!searched ? (
                <HelperText>
                  Type at least {MIN_QUERY_LENGTH} characters to search.
                </HelperText>
              ) : null}
            </Results>
          )}
        </ModalBody>
      </Modal>
    );
  }
}

export default AddDoctorModal;
